import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './apiClient';

const ivaConditionKeys = {
  all: ['ivaConditions'],
  lists: () => [...ivaConditionKeys.all, 'list'],
  details: () => [...ivaConditionKeys.all, 'detail'],
  detail: (id) => [...ivaConditionKeys.details(), id],
};

/**
 * Hook para obtener todas las condiciones de IVA
 */
export const useIvaConditions = () => {
  return useQuery({
    queryKey: ivaConditionKeys.lists(),
    queryFn: async () => {
      const response = await apiClient.get('iva_conditions');
      return response.data;
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
};

/**
 * Hook para obtener una condición de IVA específica
 * @param {string} id - UUID de la condición de IVA
 * @param {Object} options - Opciones adicionales para la query
 */
export const useIvaCondition = (id, options = {}) => {
  return useQuery({
    queryKey: ivaConditionKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.get(`iva_conditions/${id}`);
      return response.data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

/**
 * Hook para crear una nueva condición de IVA
 */
export const useCreateIvaCondition = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (ivaConditionData) => {
      const response = await apiClient.post('iva_conditions', ivaConditionData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ivaConditionKeys.lists() });
    },
    onError: (error) => {
      console.error('Error creating IVA condition:', error);
    },
  });
};

/**
 * Hook para actualizar una condición de IVA existente
 */
export const useUpdateIvaCondition = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (ivaConditionData) => {
      const cleanedData = { ...ivaConditionData };
      
      delete cleanedData.deleted_at;
      delete cleanedData.updated_at;
      delete cleanedData.created_at;
      delete cleanedData.is_active;
      delete cleanedData.modules;
      
      const response = await apiClient.put(`iva_conditions/${cleanedData.uuid}`, cleanedData);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(ivaConditionKeys.detail(data.uuid), data);
      queryClient.invalidateQueries({ queryKey: ivaConditionKeys.lists() });
    },
    onError: (error) => {
      console.error('Error updating IVA condition:', error);
    },
  });
};

/**
 * Hook para eliminar una condición de IVA
 */
export const useDeleteIvaCondition = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const response = await apiClient.delete(`iva_conditions/${id}`);
      return response.data;
    },
    onSuccess: (data, deletedId) => {
      queryClient.invalidateQueries({ queryKey: ivaConditionKeys.lists() });
      queryClient.removeQueries({ queryKey: ivaConditionKeys.detail(deletedId) });
    },
    onError: (error) => {
      console.error('Error deleting IVA condition:', error);
    },
  });
};